<script lang="ts">
	import Image from '../components/image.svelte';
	import type { SbColor, SbImage } from '../types';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<{
			image: SbImage;
			desktop_image: SbImage;
			label: string;
			description: string;
			text_alignment: 'center' | 'top' | 'bottom';
			text_color: Extract<SbColor, 'stone-5' | 'brown-2'>;
			show_overlay: boolean;
		}>['content'];
		fit: 'contain' | 'cover';
	}

	let { content, fit = 'cover' }: Props = $props();
</script>

<div use:editable={content} class="labeled-image relative h-full">
	<Image
		wrapperClass={content.desktop_image?.filename ? 'lg:hidden' : ''}
		class="h-full w-full object-{fit} object-center"
		src={content.image.filename}
		alt={content.image.alt}
		title={content.image.title}
		width={430}
		sizes={{
			430: { width: 860 }
		}}
	/>
	{#if content.desktop_image?.filename}
		<Image
			wrapperClass="max-lg:hidden"
			class="h-full w-full object-{fit} object-center"
			src={content.desktop_image.filename}
			alt={content.desktop_image.alt}
			title={content.desktop_image.title}
			width={860}
			sizes={{
				1024: { width: 1800 }
			}}
		/>
	{/if}
	{#if content.show_overlay}
		<div class="pointer-events-none absolute inset-0 bg-brown-2/20"></div>
	{/if}
	{#if content.label || content.description}
		<div
			class="pointer-events-none absolute inset-x-8 inset-y-6 text-center lg:inset-x-12 lg:inset-y-14 xl:inset-x-20 text-{content.text_color} flex flex-col items-center text-balance"
			class:justify-center={!content.text_alignment || content.text_alignment === 'center'}
			class:justify-start={content.text_alignment === 'top'}
			class:justify-end={content.text_alignment === 'bottom'}
		>
			{#if content.label}
				<div class="heading tracking-[-.56px] empty:hidden">
					<SbRichtext text={content.label} />
				</div>
			{/if}
			{#if content.description}
				<div class="subheading mt-4 empty:hidden">
					<SbRichtext text={content.description} />
				</div>
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	.labeled-image {
		div.heading {
			&:global {
				/* "p" => fallback */
				p,
				h1 {
					font-size: 32px;
					line-height: 110%;
					font-weight: theme('fontWeight.semibold');

					@media (min-width: 1024px) {
						font-size: 56px;
						line-height: 140%;
					}
				}
				h2,
				h3,
				h4,
				h5,
				h6 {
					font-size: 18px;
					line-height: 130%;
					font-weight: theme('fontWeight.normal');

					@media (min-width: 1024px) {
						font-size: 20px;
						line-height: 130%;
					}
				}
			}
		}
		div.subheading {
			&:global {
				/* "p" => fallback */
				p,
				h2,
				h3,
				h4,
				h5,
				h6 {
					font-size: 16px;
					line-height: 140%;
					font-weight: theme('fontWeight.normal');

					@media (min-width: 1024px) {
						font-size: 24px;
					}
				}
			}
		}
	}
</style>
